import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useDropzone, type FileRejection } from "react-dropzone"
import { useNavigate } from "react-router-dom"
import { CreateBidFormSchema, type CreateBidForm, type BidPriority, validateFile, getAcceptedFileTypes, normalizeFilename, formatFileSize } from "@/lib/schemas"
import { useBidsStore } from "@/stores"
import { useDocumentsStore } from "@/stores/documents-store"
import { useSharePointGate } from "@/hooks/useSharePointGate"
import { SharePointBrowser, type SharePointStagedSelection } from "@/components/SharePointBrowser"
import { SourceIcon } from "@/components/ui/source-icon"
import { importSharePointFiles } from "@/lib/api"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { TagInput } from "@/components/ui/tag-input"
import { UserPicker } from "@/components/ui/user-picker"
import { Plus, Loader2, AlertCircle, Upload, X, FileText, FolderOpen } from "lucide-react"
import { cn } from "@/lib/utils"
import { deadlineFromInputValues, inputTimeZoneName } from "@/lib/bid-deadline"
import { toast } from "sonner"
import { ARCHIVE_ACCEPT_TYPES, expandUploadFiles, type ExpandedArchive } from "@/lib/expand-upload-files"
import {
  describeAdded,
  describeOpening,
  describeSkippedLines,
  invalidFileReason,
  type SkippedUpload,
} from "@/lib/upload-feedback"
import { useFolderPicker } from "@/hooks/useFolderPicker"

/**
 * A staged SharePoint pick, flattened. The picker hands back one site/drive per
 * visit, so keeping its payload shape would mean a second visit could only
 * replace the first — with every pick now named in the list, that silent loss
 * would be visible and wrong. Carrying site/drive per item lets picks from
 * different libraries accumulate, and matches the import API's item shape.
 */
interface StagedSharePointDoc {
  siteId: string
  driveId: string
  itemId: string
  name: string
  size: number
}

/**
 * What the last drop or pick did. A drop used to give no sign it had worked:
 * the list rendered below the fold of the dialog's scroll box, and files the
 * dropzone refused (a .dwg, a .msg, anything flattened out of a dropped
 * folder) vanished without a word. So every drop now says what it added and
 * names what it skipped — including which ZIPs it opened.
 */
interface DropFeedback {
  added: number
  skipped: SkippedUpload[]
  expandedArchives: ExpandedArchive[]
}

interface CreateBidDialogProps {
  trigger?: React.ReactNode
  onSuccess?: () => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function CreateBidDialog({ trigger, onSuccess, open: controlledOpen, onOpenChange: controlledOnOpenChange }: CreateBidDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen ?? internalOpen
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [tags, setTags] = useState<string[]>([])
  const [assignedTo, setAssignedTo] = useState<string[]>([])
  const [stagedFiles, setStagedFiles] = useState<File[]>([])
  const [dropFeedback, setDropFeedback] = useState<DropFeedback | null>(null)
  const [scrollRequest, setScrollRequest] = useState(0)
  const addedFilesRef = useRef<HTMLDivElement>(null)
  const [stagedSharePoint, setStagedSharePoint] = useState<StagedSharePointDoc[]>([])
  const [sharePointBrowserOpen, setSharePointBrowserOpen] = useState(false)
  const { createBid, isLoading, users, fetchUsers, bids } = useBidsStore()
  const navigate = useNavigate()
  const { uploadTenderDocuments } = useDocumentsStore()
  const {
    isConnected: isSharePointConnected,
    ensureChecked: ensureSharePointChecked,
    openOrConnect: openOrConnectSharePoint,
  } = useSharePointGate()

  // Fetch users on mount
  useEffect(() => {
    void fetchUsers()
  }, [fetchUsers])

  // Probe SharePoint connection when the dialog opens so the button can show
  // "Import from SharePoint" vs "Connect SharePoint" without a click delay.
  useEffect(() => {
    if (open) ensureSharePointChecked()
  }, [open, ensureSharePointChecked])

  // Compute tag suggestions from existing bids
  const tagSuggestions = useMemo(() => {
    const allTags = bids.flatMap(b => b.tags ?? [])
    return [...new Set(allTags)]
  }, [bids])

  // React Hook Form with Zod resolver - using explicit generic for type safety
  const form = useForm<CreateBidForm>({
    resolver: zodResolver(CreateBidFormSchema),
    defaultValues: {
      title: "",
      client: "",
      priority: "medium",
      description: "",
    },
  })

  const {
    register,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = form

  const selectedPriority = watch("priority")

  // Read after the await in `addFiles`: a second drop can land while a ZIP is
  // still opening, and the duplicate check must see what is staged by then.
  const stagedFilesRef = useRef(stagedFiles)
  useEffect(() => {
    stagedFilesRef.current = stagedFiles
  }, [stagedFiles])
  const [openingMessage, setOpeningMessage] = useState<string | null>(null)
  // Bumped by Clear all and by closing the dialog, so a ZIP that finishes
  // opening afterwards doesn't put its files back.
  const stagingGeneration = useRef(0)
  useEffect(() => {
    if (!open) {
      stagingGeneration.current++
      setOpeningMessage(null)
    }
  }, [open])

  const addFiles = useCallback(async (incoming: File[], rejected: File[] = []) => {
    // The dropzone only rejects on `accept` (no size or count limits are set),
    // so every rejection is a file type we don't take.
    const skipped: SkippedUpload[] = rejected.map((file) => ({
      name: file.name,
      reason: "unsupported",
    }))

    const generation = stagingGeneration.current
    const opening = describeOpening(incoming)
    if (opening) setOpeningMessage(opening)
    const expanded = await expandUploadFiles(incoming)
    if (generation !== stagingGeneration.current) return
    if (opening) setOpeningMessage(null)
    skipped.push(...expanded.skipped)

    const staged = stagedFilesRef.current
    const validFiles: File[] = []
    const stagedNormalized = staged.map((f) => normalizeFilename(f.name))

    // Duplicates are matched on the extension-less name on purpose: the same
    // document in two formats (RFP.docx + RFP.pdf) is one document, and the
    // Documents tab's upload uses the same rule.
    const skipDuplicate = (file: File, match: File) => {
      skipped.push({
        name: match.name === file.name ? file.name : `${file.name} (matches ${match.name})`,
        reason: "duplicate",
      })
    }

    expanded.files.forEach((file) => {
      const normalized = normalizeFilename(file.name)

      const stagedMatch = staged.find(
        (_, idx) => stagedNormalized[idx] === normalized
      )
      if (stagedMatch) {
        skipDuplicate(file, stagedMatch)
        return
      }

      const batchMatch = validFiles.find(
        (f) => normalizeFilename(f.name) === normalized
      )
      if (batchMatch) {
        skipDuplicate(file, batchMatch)
        return
      }

      if (validateFile(file).valid) {
        validFiles.push(file)
      } else {
        skipped.push({ name: file.name, reason: invalidFileReason(file) })
      }
    })

    const hasNews =
      validFiles.length > 0 || skipped.length > 0 || expanded.expandedArchives.length > 0
    setDropFeedback(
      hasNews
        ? { added: validFiles.length, skipped, expandedArchives: expanded.expandedArchives }
        : null
    )
    if (validFiles.length > 0) {
      stagedFilesRef.current = [...staged, ...validFiles]
      setStagedFiles((prev) => [...prev, ...validFiles])
      setScrollRequest((n) => n + 1)
    }
  }, [])

  const onDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
    void addFiles(acceptedFiles, fileRejections.map(({ file }) => file))
  }, [addFiles])

  const { openFolderPicker, folderInput } = useFolderPicker({
    onFiles: (files) => void addFiles(files),
    disabled: isSubmitting || isLoading,
  })

  // The documents block is the last thing in a scroll box that is already
  // full on a laptop, so without this the list lands under the fold and the
  // drop looks like it did nothing.
  useEffect(() => {
    if (scrollRequest === 0) return
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    addedFilesRef.current?.scrollIntoView({
      block: "nearest",
      behavior: reduceMotion ? "auto" : "smooth",
    })
  }, [scrollRequest])

  const skippedLines = useMemo(
    () => (dropFeedback ? describeSkippedLines(dropFeedback.skipped) : []),
    [dropFeedback]
  )
  const addedLine = dropFeedback
    ? describeAdded(dropFeedback.added, dropFeedback.expandedArchives)
    : null

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    // ZIPs are let through here only to be opened in the browser; they are
    // never uploaded, and the server-bound allow-list does not include them.
    accept: { ...getAcceptedFileTypes(), ...ARCHIVE_ACCEPT_TYPES },
    multiple: true,
  })

  const stageSharePointSelection = useCallback((selection: SharePointStagedSelection) => {
    setStagedSharePoint((prev) => [
      ...prev,
      ...selection.items
        .filter((it) => !prev.some((doc) => doc.itemId === it.itemId))
        .map((it) => ({
          siteId: selection.siteId,
          driveId: selection.driveId,
          itemId: it.itemId,
          name: it.name,
          size: it.size,
        })),
    ])
  }, [])

  const clearStagedDocuments = useCallback(() => {
    stagingGeneration.current++
    setOpeningMessage(null)
    setStagedFiles([])
    setStagedSharePoint([])
    setDropFeedback(null)
  }, [])

  // One list, both sources. Which door a document came through doesn't change
  // what the user is checking here — that the right documents are going onto
  // the bid — so uploads and SharePoint picks are named side by side.
  const stagedDocuments = useMemo(() => {
    const local = stagedFiles.map((file, i) => ({
      key: `local-${file.name}-${file.size}-${i}`,
      name: file.name,
      size: file.size,
      source: "local" as const,
      remove: () => setStagedFiles((prev) => prev.filter((_, idx) => idx !== i)),
    }))
    const sharePoint = stagedSharePoint.map((doc) => ({
      key: `sharepoint-${doc.itemId}`,
      name: doc.name,
      size: doc.size,
      source: "sharepoint" as const,
      remove: () =>
        setStagedSharePoint((prev) => prev.filter((d) => d.itemId !== doc.itemId)),
    }))
    return [...local, ...sharePoint]
  }, [stagedFiles, stagedSharePoint])

  const onSubmit = async (data: CreateBidForm) => {
    setSubmitError(null)
    try {
      // A date alone is saved as before (UTC midnight of the date, date-only);
      // a date and time is that moment in this person's time zone.
      const { deadlineTime: _time, ...fields } = data
      const entered = deadlineFromInputValues(data.deadline, data.deadlineTime)

      const newBid = await createBid({
        ...fields,
        value: data.value ?? undefined, // Ensure NaN becomes undefined
        deadline: entered?.deadline,
        ...(entered ? { deadlineHasTime: entered.deadlineHasTime } : {}),
        // R4: a new bid starts Active · Qualify. `status` keeps a backend
        // without the stage model in step; the new fields win where they exist.
        status: "qualifying",
        lifecycle: "active",
        stage: "qualify",
        organizationId: "00000000-0000-0000-0000-000000000000", // Default org for dev
        assignedTo,
        tags,
      })

      // Fire-and-forget: uploads continue in background via documents store
      if (stagedFiles.length > 0) {
        void uploadTenderDocuments(newBid.id, stagedFiles)
        toast(`Uploading ${stagedFiles.length} document${stagedFiles.length > 1 ? "s" : ""}...`, {
          description: `Documents will be processed for "${newBid.title}"`,
        })
      }

      // Trigger the SharePoint import now that we have a bidId. We call the
      // API directly (rather than the store's importSelected) because the
      // store's selection state has already been cleared when the picker closed.
      if (stagedSharePoint.length > 0) {
        const itemsCount = stagedSharePoint.length
        void importSharePointFiles({
          destination: { type: "bid", bidId: newBid.id },
          items: stagedSharePoint.map((doc) => ({
            siteId: doc.siteId,
            driveId: doc.driveId,
            itemId: doc.itemId,
          })),
        }).catch((err) => {
          const message = err instanceof Error ? err.message : "Failed to start SharePoint import"
          toast.error(`SharePoint import failed: ${message}`)
        })
        toast(`Importing ${itemsCount} file${itemsCount > 1 ? "s" : ""} from SharePoint...`, {
          description: `Files will appear in "${newBid.title}" once processed`,
        })
      }

      reset()
      setTags([])
      setAssignedTo([])
      setStagedFiles([])
      setDropFeedback(null)
      setStagedSharePoint([])
      setOpen(false)
      onSuccess?.()
      void navigate(`/app/bids/${newBid.id}`)
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to create bid"
      setSubmitError(message)
    }
  }

  const setOpen = (value: boolean) => {
    if (controlledOnOpenChange) {
      controlledOnOpenChange(value)
    } else {
      setInternalOpen(value)
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      reset()
      setTags([])
      setAssignedTo([])
      setStagedFiles([])
      setDropFeedback(null)
      setStagedSharePoint([])
      setSubmitError(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button size="sm" className="gap-1.5 pl-2">
            <Plus className="h-4 w-4" />
            New Bid
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
          <DialogHeader>
            <DialogTitle>Create New Bid</DialogTitle>
            <DialogDescription>
              Add a new bid opportunity to your pipeline. Fill in the details below.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4 px-1 -mx-1 max-h-[60vh] overflow-y-auto">
            {/* Title */}
            <div className="grid gap-2">
              <Label htmlFor="title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                placeholder="e.g., Enterprise Software Development"
                {...register("title")}
                className={cn(errors.title && "border-destructive")}
              />
              {errors.title && (
                <p className="text-sm text-destructive">{errors.title.message}</p>
              )}
            </div>

            {/* Client */}
            <div className="grid gap-2">
              <Label htmlFor="client">
                Client <span className="text-destructive">*</span>
              </Label>
              <Input
                id="client"
                placeholder="e.g., Tech Corp Inc."
                {...register("client")}
                className={cn(errors.client && "border-destructive")}
              />
              {errors.client && (
                <p className="text-sm text-destructive">{errors.client.message}</p>
              )}
            </div>

            {/* Priority */}
            <div className="grid gap-2">
              <Label htmlFor="priority">
                Priority <span className="text-destructive">*</span>
              </Label>
              <Select
                value={selectedPriority}
                onValueChange={(value: BidPriority) => setValue("priority", value)}
              >
                <SelectTrigger className={cn(errors.priority && "border-destructive")}>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-blue-500" />
                      Low
                    </span>
                  </SelectItem>
                  <SelectItem value="medium">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-amber-500" />
                      Medium
                    </span>
                  </SelectItem>
                  <SelectItem value="high">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-orange-500" />
                      High
                    </span>
                  </SelectItem>
                  <SelectItem value="critical">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-red-500" />
                      Critical
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              {errors.priority && (
                <p className="text-sm text-destructive">{errors.priority.message}</p>
              )}
            </div>

            {/* Value and Deadline row. The deadline column sizes to its date and
                time inputs; value takes the rest. Stacked below sm. Top-aligned:
                the Deadline column is taller (it carries the time-zone hint), and
                a stretched Value column spread its label and input apart, so the
                Value input sat below the date and time inputs. */}
            <div className="grid items-start gap-4 sm:grid-cols-[minmax(0,1fr)_auto]">
              <div className="grid gap-2">
                <Label htmlFor="value">Value ($)</Label>
                <Input
                  id="value"
                  type="number"
                  placeholder="e.g., 100000"
                  {...register("value", { valueAsNumber: true })}
                  className={cn(errors.value && "border-destructive")}
                />
                {errors.value && (
                  <p className="text-sm text-destructive">{errors.value.message}</p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="deadline">Deadline</Label>
                <div className="flex gap-2">
                  <Input
                    id="deadline"
                    type="date"
                    {...register("deadline")}
                    className={cn("min-w-0 flex-1 sm:w-40 sm:flex-none", errors.deadline && "border-destructive")}
                  />
                  <Input
                    id="deadlineTime"
                    type="time"
                    aria-label="Closing time (optional)"
                    aria-describedby="deadlineTime-hint"
                    {...register("deadlineTime")}
                    className="w-32 shrink-0"
                  />
                </div>
                <p id="deadlineTime-hint" className="text-meta">
                  Time is optional, in your time zone ({inputTimeZoneName(watch("deadline"))})
                </p>
                {errors.deadline && (
                  <p className="text-sm text-destructive">{errors.deadline.message}</p>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Brief description of the bid opportunity..."
                rows={3}
                {...register("description")}
                className={cn(errors.description && "border-destructive")}
              />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description.message}</p>
              )}
            </div>

            {/* Tags */}
            <div className="grid gap-2">
              <Label>Tags</Label>
              <TagInput value={tags} onChange={setTags} suggestions={tagSuggestions} placeholder="Add tags..." />
            </div>

            {/* Assigned Users */}
            <div className="grid gap-2">
              <Label>Assigned to</Label>
              <UserPicker value={assignedTo} onChange={setAssignedTo} users={users} />
            </div>

            {/* Tender Documents */}
            <div className="grid gap-2">
              <Label>Tender Documents</Label>
              {/* min-w-0: the staged names are `truncate`, i.e. nowrap, so
                  their min-content width is the whole string. As a grid item
                  this box would default to min-width:auto and grow to fit
                  them, stretching the dialog's grid track past
                  sm:max-w-[600px] — every field and the footer then render
                  wider than the dialog card itself. */}
              <div className="min-w-0">
                <div
                  {...getRootProps()}
                  className={cn(
                    "min-w-0 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors",
                    isDragActive
                      ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5"
                      : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/50",
                    (isSubmitting || isLoading) && "pointer-events-none opacity-50"
                  )}
                >
                  <input {...getInputProps()} disabled={isSubmitting || isLoading} />
                  <Upload className="mx-auto h-6 w-6 text-[hsl(var(--muted-foreground))]" />
                  <p className="mt-2 text-xs font-medium">
                    {isDragActive ? "Drop files here..." : "Drop RFP/tender documents or click to browse"}
                  </p>
                  <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
                    PDF, Word, Excel, PowerPoint, CSV, TXT — or a ZIP or folder
                  </p>
                  {/* Stops the click reaching the drop zone, which would open
                      the file picker on top of the folder picker. */}
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="mt-1 h-auto gap-1.5 p-0 text-xs"
                    disabled={isSubmitting || isLoading}
                    onClick={(e) => {
                      e.stopPropagation()
                      openFolderPicker()
                    }}
                  >
                    <FolderOpen className="h-4 w-4" />
                    Upload folder
                  </Button>
                </div>
                {folderInput}

                {/* Scroll target after a drop: the confirmation plus the list.
                    Always mounted (empty = no height) so the live region
                    exists before its text changes, which is when screen
                    readers announce it. */}
                <div ref={addedFilesRef} className="min-w-0">
                  <div aria-live="polite">
                    {openingMessage && (
                      <p className="mt-2 flex items-center gap-1.5 text-meta">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        {openingMessage}
                      </p>
                    )}
                    {dropFeedback && (
                      <div className="mt-2 space-y-1">
                        {addedLine && (
                          <p className="text-meta">{addedLine}</p>
                        )}
                        {skippedLines.map((line) => (
                          <p
                            key={line}
                            className={cn(
                              "text-meta",
                              dropFeedback.added === 0 && "text-destructive"
                            )}
                          >
                            {line}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Named, individually removable, whichever source they came
                      from. A bare count can't tell you whether you grabbed the
                      right documents out of a tender pack, and one
                      clear-everything button makes a single wrong pick cost you
                      the whole selection.

                      Sits directly under the drop zone, above SharePoint, so the
                      files a drop added are next to where they were dropped. */}
                  {stagedDocuments.length > 0 && (
                    <div className="mt-2 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium">
                          {stagedDocuments.length} file{stagedDocuments.length > 1 ? "s" : ""} selected
                        </span>
                        <button
                          type="button"
                          onClick={clearStagedDocuments}
                          className="text-xs text-[hsl(var(--muted-foreground))] hover:text-destructive"
                          disabled={isSubmitting || isLoading}
                        >
                          Clear all
                        </button>
                      </div>
                      <ul className="mt-2 space-y-1">
                        {stagedDocuments.map((doc) => (
                          <li
                            key={doc.key}
                            className="flex items-center gap-2 rounded-md bg-[hsl(var(--muted))] px-2 py-1"
                          >
                            {doc.source === "sharepoint" ? (
                              <SourceIcon source="sharepoint" size={14} className="shrink-0" />
                            ) : (
                              <FileText className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--muted-foreground))]" />
                            )}
                            <span className="min-w-0 flex-1 truncate text-xs" title={doc.name}>
                              {doc.name}
                            </span>
                            {doc.size > 0 && (
                              <span className="shrink-0 text-[11px] text-[hsl(var(--muted-foreground))] tabular-nums">
                                {formatFileSize(doc.size)}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={doc.remove}
                              className="shrink-0 text-[hsl(var(--muted-foreground))] hover:text-destructive"
                              disabled={isSubmitting || isLoading}
                              aria-label={`Remove ${doc.name}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* SharePoint option — stops the form click from propagating to the
                  dropzone so the file picker doesn't open. */}
              <div
                className="flex items-center justify-between gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="text-xs text-[hsl(var(--muted-foreground))]">
                  Or import from SharePoint
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-1.5"
                  disabled={isSubmitting || isLoading}
                  onClick={() => openOrConnectSharePoint(() => setSharePointBrowserOpen(true))}
                  data-testid="create-bid-sharepoint-button"
                >
                  <SourceIcon source="sharepoint" size={14} />
                  {isSharePointConnected ? "Browse SharePoint" : "Connect SharePoint"}
                </Button>
              </div>
            </div>
          </div>

          {submitError && (
            <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {submitError}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting || isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || isLoading}>
              {(isSubmitting || isLoading) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {createButtonLabel({ files: stagedFiles.length, sp: stagedSharePoint.length })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* SharePoint picker — lazy-mounted, stages selection back to the dialog. */}
      {sharePointBrowserOpen && (
        <SharePointBrowser
          open={sharePointBrowserOpen}
          onClose={() => setSharePointBrowserOpen(false)}
          mode="pick"
          onStage={(selection) => {
            stageSharePointSelection(selection)
            setSharePointBrowserOpen(false)
          }}
        />
      )}
    </Dialog>
  )
}

function createButtonLabel({ files, sp }: { files: number; sp: number }): string {
  if (files === 0 && sp === 0) return "Create Bid"
  if (files > 0 && sp === 0) return "Create Bid & Upload"
  if (files === 0 && sp > 0) return "Create Bid & Import"
  return "Create Bid & Add Files"
}
